import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TabWithQuery } from '@pages/Trade/Analytics/Analytics.types'
import { TopAnalyticsItem } from '@pages/Trade/Analytics/hooks/useAnalyticsFetch'
import { computeChangePercent } from '@pages/Trade/Markets/Instruments/hooks/useChangePercent'
import { useCurrentPriceForTrade } from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import { isNumber } from '@pages/Trade/Portfolio/utils/tpslHelpers'
import { usePriceColor } from '@pages/Trade/hooks/usePriceColor'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body2, Caption1, Title2 } from '@ui/Typography'
import { Divider } from '@ui/components/Divider'
import { Color, ProgressBar } from '@ui/components/ProgressBar'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { Tooltip } from '@ui/components/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'

import { useWindowResize } from '@utils/domUtils'

import {
  isAnalyticsBuyRatioItem,
  isAnalyticsFollowRatioItem,
  isAnalyticsPriceChangeItem,
  isAnalyticsSellRatioItem,
  isAnalyticsVolatilityItem,
} from '@model/AnalyticsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './Analytics.page.module.scss'

interface WidgetCardHeaderProps {
  title: string
  activeTab?: string
  tabs?: TabWithQuery[]
  onClick?: () => void
}

export const WidgetCardHeader: React.FC<WidgetCardHeaderProps> = (props) => {
  const { title, activeTab, tabs, onClick } = props

  const isMobile = useWindowResize()

  const content = [
    <HeaderTitle title={title} onClick={onClick} />,
    !!tabs?.length && (
      <Box>
        <WidgetCardHeaderTabs activeTab={activeTab} tabs={tabs} />
      </Box>
    ),
  ]

  if (isMobile) {
    return (
      <Box
        flex
        pl='x20'
        pr='x20'
        py='x16'
        justify='space-between'
        fullWidth
        direction='column'
        minHeight={40}
      >
        {content}
      </Box>
    )
  }

  return (
    <Box
      flex
      pl='x20'
      pr='x12'
      py='x8'
      justify='space-between'
      fullWidth
      direction='row'
      minHeight={40}
      gap='x8'
      align='center'
    >
      {content}
    </Box>
  )
}

interface WidgetCardHeaderTabsProps {
  activeTab?: string
  tabs?: TabWithQuery[]
  onClick?: () => void
}

const WidgetCardHeaderTabs: React.FC<WidgetCardHeaderTabsProps> = (props) => {
  const { activeTab, tabs } = props
  return <SegmentedControl size='md' variant='default' activeTab={activeTab} tabs={tabs} />
}

const HeaderTitle: React.FC<{ title: string; onClick?: () => void }> = (props) => {
  const { title, onClick } = props

  return (
    <Box minWidth={0}>
      <Title2
        semibold
        color='text-high-em'
        onClick={onClick}
        className={onClick ? 'has-cursor-pointer' : undefined}
      >
        <Box flex align='center' py='x12' minWidth={0}>
          <Truncate>{title}</Truncate>
          <CaretIcon direction='right' color='text-high-em' size={20} />
        </Box>
      </Title2>
    </Box>
  )
}

interface WidgetCardBodyProps {
  limit?: number
  data: TopAnalyticsItem[]
  onInstrumentClick?: (symbol: string) => void
  progressBarColor?: Color
}

export const WidgetCardBody: React.FC<WidgetCardBodyProps> = (props) => {
  const { limit, data, onInstrumentClick, progressBarColor = 'purple' } = props

  const totalCount = data.length || 0
  const itemsToShow = limit ? Math.min(limit, totalCount) : totalCount

  return (
    <Box flex direction='column' fullWidth>
      {Array.from({ length: itemsToShow }, (_, index) => data[index]).map((x, index) => (
        <React.Fragment key={x.instrumentSymbol || index}>
          {index > 0 && (
            <Box py='x12' fullWidth>
              <Divider.Horizontal />
            </Box>
          )}
          <Box
            flex
            fullWidth
            justify='space-between'
            className={classNames(styles.widgetCardRow, {
              [styles.widgetCardRowClickable]: !!onInstrumentClick,
            })}
            onClick={
              onInstrumentClick && x.instrumentSymbol
                ? () => onInstrumentClick(x.instrumentSymbol!)
                : undefined
            }
          >
            <WidgetCardBodyContent data={x} progressBarColor={progressBarColor} />
          </Box>
        </React.Fragment>
      ))}
    </Box>
  )
}

interface WidgetCardBodyContentProps {
  data: TopAnalyticsItem
  progressBarColor?: Color
}

const WidgetCardBodyContent: React.FC<WidgetCardBodyContentProps> = React.memo((props) => {
  const { data, progressBarColor = 'purple' } = props

  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const isMobile = useWindowResize()

  const tooltipText = getAnalyticsTooltipText(data)
  const percentage = getAnalyticsPercentage(data)
  const priceStepDecimals = getStepDecimals(data.instrument?.priceIncrement)

  const livePrice = useCurrentPriceForTrade(data.instrumentSymbol ?? null, 'BUY', 'position')
  const displayPrice = livePrice ?? null
  const color = usePriceColor(livePrice)

  const displayChangePercent = useMemo(() => {
    if (livePrice != null && data.latestDailyCandle != null) {
      return computeChangePercent(livePrice, data.latestDailyCandle.openingPrice, false)
    }
    return null
  }, [livePrice, data.latestDailyCandle])

  const formattedPrice = isNumber(displayPrice)
    ? formatNumber(displayPrice, {
        minDecimalPlaces: priceStepDecimals,
        maxDecimalPlaces: priceStepDecimals,
      })
    : '-'

  return (
    <>
      <Box flex direction='column'>
        <Body2 medium color='text-high-em'>
          {data.instrumentSymbol}
        </Body2>
        <Caption1
          medium
          color='text-low-em'
          onClick={isMobile ? (event) => event.stopPropagation() : undefined}
        >
          <Truncate>{data.instrumentName}</Truncate>
        </Caption1>
      </Box>

      <Box flex align='center' fullWidth maxWidth={92} style={{ margin: 'auto' }}>
        <Tooltip direction='top' content={t(tooltipText, { percentage })} fullWidth>
          <Box
            flex
            align='center'
            fullWidth
            gap='x6'
            onClick={isMobile ? (e) => e.stopPropagation() : undefined}
          >
            <ProgressBar
              color={progressBarColor}
              percent={percentage}
              size='small'
              hasMinVisibleWidth
            />
            <Caption1 medium color='text-med-em'>
              {percentage}%
            </Caption1>
          </Box>
        </Tooltip>
      </Box>

      <Box flex direction='column'>
        <Box flex justify='flex-end'>
          <Body2 medium color={color} className={styles.numericValue}>
            {formattedPrice}
          </Body2>
        </Box>

        {displayChangePercent?.label && (
          <Caption1 medium color={displayChangePercent?.color} className={styles.numericValue}>
            <Box flex align='center' justify='flex-end'>
              <ArrowIcon
                direction={displayChangePercent.isPriceUp ? 'up' : 'down'}
                color={displayChangePercent.color}
                size={16}
              />
              {displayChangePercent.label}
            </Box>
          </Caption1>
        )}
      </Box>
    </>
  )
})

const getAnalyticsTooltipText = (item: TopAnalyticsItem): string => {
  if (isAnalyticsPriceChangeItem(item)) {
    return 'Analytics.Rose {{percentage}}% during the period'
  }

  if (isAnalyticsBuyRatioItem(item)) {
    return 'Analytics.{{percentage}}% of open trades are Buy'
  }

  if (isAnalyticsSellRatioItem(item)) {
    return 'Analytics.{{percentage}}% of open trades are Sell'
  }

  if (isAnalyticsVolatilityItem(item)) {
    return 'Analytics.{{percentage}}% price fluctuation around average'
  }

  if (isAnalyticsFollowRatioItem(item)) {
    return 'Analytics.Added to Favorites by {{percentage}}% of traders'
  }

  return ''
}

export const getProgressBarColor = (activeTab?: string): Color => {
  if (activeTab === 'risers' || activeTab === 'bought') return 'green'
  if (activeTab === 'fallers' || activeTab === 'sold') return 'red'
  return 'purple'
}

const formatAnalyticsPercentage = (value: number | null | undefined): number => {
  if (value == null || value === 0) return 0

  const absoluteValue = Math.abs(value)

  if (absoluteValue >= 1) return Math.round(absoluteValue)

  return Math.max(0.1, Math.round(absoluteValue * 10) / 10)
}

const getAnalyticsPercentage = (item: TopAnalyticsItem): number => {
  if (isAnalyticsPriceChangeItem(item)) {
    return formatAnalyticsPercentage(item.averagePriceChangePercentage)
  }

  if (isAnalyticsBuyRatioItem(item)) {
    return formatAnalyticsPercentage(item.buyRatioPercentage)
  }

  if (isAnalyticsSellRatioItem(item)) {
    return formatAnalyticsPercentage(item.sellRatioPercentage)
  }

  if (isAnalyticsVolatilityItem(item)) {
    return formatAnalyticsPercentage(item.averageVolatilityPercentage)
  }

  if (isAnalyticsFollowRatioItem(item)) {
    return formatAnalyticsPercentage(item.followInstrumentRatio)
  }

  return 0
}
